const NO_FILTER = () => true;
const SECRET_KEY_PATH_SEGMENT = "<secret-key>";
export const DEFAULT_SECRET_INVENTORY_LIMIT = 100;

const indexAfterCodePoints = (text, start, count) => {
    let index = start;
    let remaining = count;
    while (index < text.length && remaining > 0) {
        index += text.codePointAt(index) > 0xFFFF ? 2 : 1;
        remaining -= 1;
    }
    return index;
};

const BASE64_RUN_START = /[A-Za-z0-9+/=]{40}/;
const BASE64_CHARACTER = /[A-Za-z0-9+/=]/;
const PYTHON_WHITESPACE_CHARACTER = /^[\p{White_Space}\u001C-\u001F]$/u;

const matchPemBody = (text, bodyStart, footer) => {
    const prefixEnd = indexAfterCodePoints(text, bodyStart, 4096 + 40);
    const prefix = text.slice(bodyStart, prefixEnd);
    const runOffset = prefix.search(BASE64_RUN_START);
    if (runOffset < 0) {
        return null;
    }

    let runEnd = bodyStart + runOffset + 40;
    while (runEnd < text.length && BASE64_CHARACTER.test(text[runEnd])) {
        runEnd += 1;
    }

    let footerStart = text.indexOf("-----", runEnd);
    const latestFooterStart = indexAfterCodePoints(text, runEnd, 4096);
    while (footerStart >= 0 && footerStart <= latestFooterStart) {
        footer.lastIndex = footerStart;
        const matchedFooter = footer.exec(text);
        if (matchedFooter) {
            return matchedFooter.index + matchedFooter[0].length;
        }
        footerStart = text.indexOf("-----", footerStart + 1);
    }
    return null;
};

const boundedPemMatches = (text, headerPattern, footerPattern) => {
    const headers = new RegExp(headerPattern.source, headerPattern.flags);
    const footer = new RegExp(footerPattern.source, footerPattern.flags);
    const matches = [];

    let header = headers.exec(text);
    while (header) {
        const end = matchPemBody(text, header.index + header[0].length, footer);
        if (end === null) {
            headers.lastIndex = header.index + 1;
            header = headers.exec(text);
            continue;
        }

        matches.push({ index: header.index });
        headers.lastIndex = end;
        header = headers.exec(text);
    }
    return matches;
};

const privateKeyMatches = (text) => {
    const maxHeaderScan = 8192;
    const begins = /-----[\p{White_Space}\u001C-\u001F]*BEGIN/gu;
    const footer = /-----[\p{White_Space}\u001C-\u001F]*END[ A-Z0-9_-]*?PRIVATE KEY[\p{White_Space}\u001C-\u001F]*-----/uy;
    const matches = [];

    let begin = begins.exec(text);
    while (begin) {
        let phraseStart = begin.index + begin[0].length;
        const scanLimit = Math.min(text.length, phraseStart + maxHeaderScan);
        let matchedEnd = null;
        while (phraseStart < scanLimit && /[ A-Z0-9_-]/.test(text[phraseStart])) {
            if (text.startsWith("PRIVATE KEY", phraseStart)) {
                let delimiter = phraseStart + "PRIVATE KEY".length;
                while (delimiter < text.length) {
                    const character = String.fromCodePoint(text.codePointAt(delimiter));
                    if (!PYTHON_WHITESPACE_CHARACTER.test(character)) {
                        break;
                    }
                    delimiter += character.length;
                }
                if (text.startsWith("-----", delimiter)) {
                    matchedEnd = matchPemBody(text, delimiter + 5, footer);
                    if (matchedEnd !== null) {
                        break;
                    }
                }
            }
            phraseStart += 1;
        }

        if (matchedEnd === null) {
            // Every candidate phrase in this permitted run was already
            // checked. Advancing past the run prevents marker-only input from
            // making the main thread rescan the same suffix quadratically.
            begins.lastIndex = Math.max(begin.index + 1, phraseStart);
        } else {
            matches.push({ index: begin.index });
            begins.lastIndex = matchedEnd;
        }
        begin = begins.exec(text);
    }
    return matches;
};

const DETECTORS = [
    {
        kind: "private_key",
        keywords: ["-----begin", "private key-----"],
        matches: privateKeyMatches,
    },
    {
        kind: "jwt",
        keywords: ["eyj", "ewog", "ewoi"],
        pattern: /(?<![\p{Letter}\p{Number}_])(?:eyJ|ewogIC|ewoid)[A-Za-z0-9_-]{12,}={0,2}\.(?:eyJ|ewo)[A-Za-z0-9_-]{12,}={0,2}\.[A-Za-z0-9_-]{11,}[A-Za-z0-9_](?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "bearer_token",
        // The pattern accepts the full Python whitespace set, so the cheap
        // prefilter must not bake in one ASCII separator.
        keywords: ["bearer"],
        pattern: /[Bb]earer[\p{White_Space}\u001C-\u001F]+([A-Za-z0-9_\-/.+~]{20,})/gu,
    },
    {
        kind: "basic_auth",
        keywords: ["basic"],
        pattern: /[Bb]asic[\p{White_Space}\u001C-\u001F]+([A-Za-z0-9+/]{20,}={0,2})/gu,
    },
    {
        kind: "aws_access_key",
        keywords: ["akia", "asia", "abia", "acca"],
        pattern: /(?<![\p{Letter}\p{Number}_])(?:AKIA|ASIA|ABIA|ACCA)[0-9A-Z]{16}(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "aws_secret_key",
        keywords: ["aws_secret", "secret_access_key", "secretaccesskey", "secret access key"],
        // `SecretAccessKey` (credentials JSON) and `AWS Secret Access Key [None]:` (aws configure) are
        // the spellings AWS prints; the optional bracket is the configure prompt's default.
        pattern: /(?:aws_secret_access_key|secret_access_key|aws_secret|secretaccesskey|secret access key)(?:[\p{White_Space}\u001C-\u001F]*\[[^\]\n]{0,40}\])?[\p{White_Space}\u001C-\u001F:='"]+([A-Za-z0-9/+=]{40})/giu,
        accepts: (text, match) => !/[A-Za-z0-9/+=]/.test(text[match.index + match[0].length] || ""),
    },
    {
        kind: "azure_devops_pat",
        keywords: ["azdo"],
        pattern: /(?<![\p{Letter}\p{Number}_./-])[A-Za-z0-9]{75}AZDO[A-Za-z0-9]{5}(?![\p{Letter}\p{Number}_./-])/gu,
    },
    {
        kind: "azure_devops_pat",
        keywords: ["azure", "dev.azure.com"],
        pattern: /(?<![\p{Letter}\p{Number}_./-])[A-Za-z0-9]{52}(?![\p{Letter}\p{Number}_./-])/gu,
    },
    {
        kind: "gcp_gemini_api_key",
        keywords: ["aiza"],
        pattern: /(?<![\p{Letter}\p{Number}_])AIza[0-9A-Za-z_-]{34}[0-9A-Za-z_](?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "gcp_gemini_api_key",
        keywords: ["aq."],
        pattern: /(?<![\p{Letter}\p{Number}_])AQ\.[A-Za-z0-9_-]{49}[A-Za-z0-9_](?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "gcp_service_account",
        keywords: ["provider_x509", "private_key_id"],
        pattern: /\{[^{]{0,4096}auth_provider_x509_cert_url[^}]{0,2000}\}/gsu,
    },
    {
        kind: "openai_api_key",
        keywords: ["sk-", "t3blbkfj"],
        pattern: /(?<![\p{Letter}\p{Number}_])sk-(?:(?:proj|svcacct|service)-[A-Za-z0-9_-]+-)?[A-Za-z0-9_-]{39,}[A-Za-z0-9_](?![\p{Letter}\p{Number}_])/gu,
        accepts: (_text, match) => match[0].includes("T3BlbkFJ"),
    },
    {
        kind: "anthropic_api_key",
        keywords: ["sk-ant-"],
        pattern: /(?<![\p{Letter}\p{Number}_])sk-ant-(?:admin01|api03)-[A-Za-z0-9_-]{80,110}[A-Za-z]{2}(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "huggingface_token",
        keywords: ["hf_", "api_org_"],
        pattern: /(?<![\p{Letter}\p{Number}_])(?:hf_|api_org_)[a-zA-Z0-9]{34}(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "github_token",
        keywords: ["ghp_", "gho_", "ghu_", "ghs_", "ghr_", "github_pat_"],
        pattern: /(?<![\p{Letter}\p{Number}_])(?:ghp|gho|ghu|ghs|ghr|github_pat)_[a-zA-Z0-9_]{36,255}(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "gitlab_token",
        keywords: [
            "glpat-", "glptt-", "gldt-", "gloas-", "glffct-", "glft-", "glimt-", "glagent-",
            "glrt-", "glcbt-", "glsoat-", "gr1348941",
        ],
        pattern: /(?<![\p{Letter}\p{Number}_])(?:(?:glpat|glptt|gldt|gloas|glffct|glft|glimt|glagent|glrt|glcbt|glsoat)-[A-Za-z0-9_-]{19,}[A-Za-z0-9_]|GR1348941[A-Za-z0-9_-]{19,}[A-Za-z0-9_])(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "slack_token",
        keywords: ["xoxb-", "xoxp-", "xoxa-", "xoxr-", "xoxe-"],
        pattern: /(?<![\p{Letter}\p{Number}_])xox[bpare]-[0-9]{10,13}-[0-9]{10,13}(?:[a-zA-Z0-9\-]*[a-zA-Z0-9])?(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "stripe_key",
        keywords: ["sk_live_", "rk_live_", "pk_live_"],
        pattern: /(?<![\p{Letter}\p{Number}_])[srp]k_live_[a-zA-Z0-9]{20,247}(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "sendgrid_key",
        keywords: ["sg."],
        pattern: /(?<![\p{Letter}\p{Number}_])SG\.[A-Za-z0-9_-]{20,24}\.[A-Za-z0-9_-]{38,49}[A-Za-z0-9_](?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "twilio_api_key",
        keywords: ["twilio", "sk"],
        pattern: /(?<![\p{Letter}\p{Number}_])SK[0-9a-fA-F]{32}(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "mailgun_key",
        keywords: ["key-"],
        pattern: /(?<![\p{Letter}\p{Number}_])key-[0-9a-f]{32}(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "npm_token",
        keywords: ["npm_"],
        pattern: /(?<![\p{Letter}\p{Number}_])npm_[A-Za-z0-9]{36}(?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "pypi_token",
        keywords: ["pypi-"],
        pattern: /(?<![\p{Letter}\p{Number}_])pypi-[A-Za-z0-9_-]{49,}[A-Za-z0-9_](?![\p{Letter}\p{Number}_])/gu,
    },
    {
        kind: "generic_secret",
        keywords: [
            "api_key", "apikey", "api-key", "secret_key", "secretkey", "secret-key",
            "access_token", "accesstoken", "access-token",
            "auth_token", "authtoken", "auth-token",
            "client_secret", "clientsecret", "client-secret", "password", "passwd",
        ],
        pattern: /(?:api[_-]?key|secret[_-]?key|access[_-]?token|auth[_-]?token|client[_-]?secret|password|passwd)['"]?[\p{White_Space}\u001C-\u001F]*[:=][\p{White_Space}\u001C-\u001F]*['"]?([^\p{White_Space}\u001C-\u001F'"]{20,})['"]?/giu,
    },
    {
        kind: "connection_string",
        keywords: [
            "mongodb+srv://", "postgres://", "postgresql://", "mysql://",
            "redis://", "rediss://", "amqp://", "amqps://", "ftp://", "sftp://", "jdbc:",
        ],
        pattern: /(?<![\p{Letter}\p{Number}_])(?:mongodb\+srv|postgres(?:ql)?|mysql|rediss?|amqps?|s?ftp):\/\/[^\p{White_Space}\u001C-\u001F'"]{10,}|(?<![\p{Letter}\p{Number}_])[Jj][Dd][Bb][Cc]:[\p{Letter}\p{Number}_]{3,10}:[^\p{White_Space}\u001C-\u001F"'<>]{10,200}/gu,
    },
    {
        kind: "url_credentials",
        keywords: ["@"],
        pattern: /(?:https?|s?ftps?|ssh|scp):\/\/[^:@/?#\p{White_Space}\u001C-\u001F'"<>`]+:[^:@/?#\p{White_Space}\u001C-\u001F'"<>`]{5,}@[^/?#\p{White_Space}\u001C-\u001F'"<>`]+/gu,
    },
    {
        kind: "pgp_private_key",
        keywords: ["begin pgp private"],
        matches: (text) => boundedPemMatches(
            text,
            /-----BEGIN PGP PRIVATE KEY BLOCK-----/g,
            /-----END PGP PRIVATE KEY BLOCK-----/y,
        ),
    },
    {
        kind: "putty_private_key",
        // `(?=(X+))\\1` emulates Python's atomic `(?>X+)`: JS has no atomic
        // groups, and without it a header with no `Private-MAC:` trailer
        // backtracks through the encryption value once per character. Only
        // `match.index` is read from this pattern, so the added group is inert.
        // The footer scan is bounded to 4096 like the Python copies: unbounded,
        // many trailerless headers rescan the whole remainder once per header.
        keywords: ["putty-user-key-file"],
        pattern: /PuTTY-User-Key-File-[23]:[\p{White_Space}\u001C-\u001F]*(?:ssh-(?:rsa|dss|ed25519|ed448)|ecdsa-sha2-nistp(?:256|384|521))[\p{White_Space}\u001C-\u001F]*Encryption:[\p{White_Space}\u001C-\u001F]*(?=([^\p{White_Space}\u001C-\u001F]+))\1[\s\S]{0,4096}?Private-MAC:/gu,
    },
    // `sshpass -p <password>`; the capture is inert here (only match.index is read).
    {
        kind: "sshpass_password",
        keywords: ["sshpass"],
        pattern: /sshpass[\p{White_Space}\u001C-\u001F]+-p[\p{White_Space}\u001C-\u001F]+['"]?([^\p{White_Space}\u001C-\u001F'"]{8,})['"]?/gu,
    },
    // X / Twitter app-only bearer token: 21 leading `A`s then 100+ URL-encoded characters.
    {
        kind: "twitter_bearer_token",
        keywords: ["aaaaaaaaaaaaaaaaaaaaa"],
        pattern: /(?<![\p{Letter}\p{Number}_])AAAAAAAAAAAAAAAAAAAAA[A-Za-z0-9%]{60,}/gu,
    },
    // OAuth 1.0a user access token: numeric user id, hyphen, 40-character body.
    {
        kind: "twitter_access_token",
        keywords: ["twitter", "tweepy", "oauth_token", "access_token_secret", "api.x.com"],
        pattern: /(?<![\p{Letter}\p{Number}_])[0-9]{7,20}-[A-Za-z0-9]{40}(?![\p{Letter}\p{Number}_])/gu,
    },
    // Consumer (API) key / secret assignments.
    {
        kind: "twitter_api_key",
        keywords: [
            "consumer_key", "consumer-key", "consumer_secret", "consumer-secret",
            "twitter_api", "twitter-api",
        ],
        pattern: /(?:consumer[_-]?(?:api[_-]?)?(?:key|secret)|twitter[_-]?(?:api[_-]?)?(?:key|secret))['"]?[\p{White_Space}\u001C-\u001F]*[:=][\p{White_Space}\u001C-\u001F]*['"]?([A-Za-z0-9]{18,60})(?![\p{Letter}\p{Number}_])/giu,
    },
    // Short env-style assignments: an UPPER_CASE environment-style name is the discriminator,
    // matched case-sensitively. 12-19 character values; 20+ stays with `generic_secret`.
    {
        kind: "env_secret",
        keywords: ["password", "passwd", "secret", "token", "api_key", "apikey"],
        pattern: /(?<![\p{Letter}\p{Number}_])[A-Z][A-Z0-9_]*(?:PASSWORD|PASSWD|SECRET|TOKEN|API_?KEY)['"]?[\p{White_Space}\u001C-\u001F]*[:=][\p{White_Space}\u001C-\u001F]*['"]?([^\p{White_Space}\u001C-\u001F'"\\$<>{}()\[\]]{11,18}[A-Za-z0-9])(?=[\p{White_Space}\u001C-\u001F'"\\]|$)/gu,
    },
    // 6-11 character env-style passwords (`MINIO_ROOT_PASSWORD=minioadmin`).
    {
        kind: "short_password",
        keywords: ["password", "passwd"],
        pattern: /(?<![\p{Letter}\p{Number}_])[A-Z][A-Z0-9_]*(?:PASSWORD|PASSWD)['"]?[\p{White_Space}\u001C-\u001F]*[:=][\p{White_Space}\u001C-\u001F]*['"]?([^\p{White_Space}\u001C-\u001F'"\\$<>{}()\[\]]{5,10}[A-Za-z0-9])(?=[\p{White_Space}\u001C-\u001F'"\\]|$)/gu,
    },
];

const findingsInText = (text) => {
    if (!text) return [];
    const lowered = text.toLowerCase();
    const findings = [];
    for (const detector of DETECTORS) {
        if (detector.keywords.length > 0 && !detector.keywords.some((keyword) => lowered.includes(keyword))) {
            continue;
        }
        const matches = detector.matches
            ? detector.matches(text)
            : text.matchAll(new RegExp(detector.pattern.source, detector.pattern.flags));
        for (const match of matches) {
            if ((detector.accepts || NO_FILTER)(text, match)) {
                findings.push({ kind: detector.kind, index: match.index });
            }
        }
    }
    return findings;
};

const STRUCTURAL_SECRET_KEYS = new Set([
    "api_key", "apikey", "api-key", "secret_key", "secretkey", "secret-key",
    "access_token", "accesstoken", "access-token",
    "auth_token", "authtoken", "auth-token",
    "client_secret", "clientsecret", "client-secret", "password", "passwd",
]);

const hasStructuralGenericSecret = (key, value) => {
    if (typeof value !== "string" || !STRUCTURAL_SECRET_KEYS.has(key.toLowerCase())) {
        return false;
    }
    return findingsInText(`${key}=${value}`)
        .some((finding) => finding.kind === "generic_secret" && finding.index === 0);
};

const escapeJsonPointerSegment = (value) => value.replaceAll("~", "~0").replaceAll("/", "~1");

const renderJsonPointer = (segments) => (
    segments.length === 0 ? "" : `/${segments.join("/")}`
);

const recordFinding = (inventory, kind, pathSegments) => {
    const candidate = { kind, json_pointer: renderJsonPointer(pathSegments) };
    const identity = `${candidate.json_pointer}\u0000${candidate.kind}`;
    if (inventory.seen.has(identity)) {
        return;
    }
    inventory.seen.add(identity);
    inventory.total += 1;
    if (inventory.limit === 0) {
        return;
    }

    if (inventory.findings.length < inventory.limit) {
        inventory.findings.push(candidate);
        return;
    }

    let largestIndex = 0;
    for (let index = 1; index < inventory.findings.length; index += 1) {
        if (compareFindings(inventory.findings[largestIndex], inventory.findings[index]) < 0) {
            largestIndex = index;
        }
    }
    if (compareFindings(candidate, inventory.findings[largestIndex]) < 0) {
        inventory.findings[largestIndex] = candidate;
    }
};

const collectFindings = (value, pathSegments, inventory) => {
    if (typeof value === "string") {
        for (const kind of new Set(findingsInText(value).map((finding) => finding.kind))) {
            recordFinding(inventory, kind, pathSegments);
        }
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((entry, index) => {
            pathSegments.push(String(index));
            collectFindings(entry, pathSegments, inventory);
            pathSegments.pop();
        });
        return;
    }
    if (value && typeof value === "object") {
        Object.keys(value).forEach((key) => {
            const keyFindings = findingsInText(key);
            const keyKinds = new Set(keyFindings.map((finding) => finding.kind));
            const segment = keyFindings.length > 0
                ? SECRET_KEY_PATH_SEGMENT
                : escapeJsonPointerSegment(key);
            pathSegments.push(segment);
            for (const kind of keyKinds) {
                recordFinding(inventory, kind, pathSegments);
            }
            if (hasStructuralGenericSecret(key, value[key])) {
                recordFinding(inventory, "generic_secret", pathSegments);
            }
            collectFindings(value[key], pathSegments, inventory);
            pathSegments.pop();
        });
    }
};

const compareFindings = (left, right) => (
    left.json_pointer < right.json_pointer ? -1
        : left.json_pointer > right.json_pointer ? 1
            : left.kind < right.kind ? -1
                : left.kind > right.kind ? 1
                    : 0
);

export function inventoryDefinitionSecrets(
    definition,
    { limit = DEFAULT_SECRET_INVENTORY_LIMIT } = {},
) {
    if (!Number.isSafeInteger(limit) || limit < 0) {
        throw new TypeError("limit must be a non-negative safe integer");
    }

    const inventory = {
        findings: [],
        limit,
        seen: new Set(),
        total: 0,
    };
    collectFindings(definition, [], inventory);
    inventory.findings.sort(compareFindings);

    return {
        findings: inventory.findings,
        total: inventory.total,
        truncated: inventory.total > limit,
    };
}
